/**
 * JSX Runtime Shim for WordPress
 *
 * PROBLEM:
 * React Query (and other modern React libraries) are compiled with the new JSX transform,
 * which expects runtime functions from 'react/jsx-runtime' (jsx, jsxs, Fragment, etc.).
 * However, WordPress's @wordpress/element doesn't expose these JSX runtime functions directly.
 *
 * SOLUTION:
 * This shim bridges the gap by:
 * 1. Using WordPress's global React instance (window.React from wp-element)
 * 2. Providing the JSX runtime API that React Query expects
 * 3. Translating modern JSX calls back to React.createElement calls
 *
 * This ensures React Query uses the SAME React instance as WordPress, avoiding the
 * "Cannot read properties of null" errors that occur when multiple React instances exist.
 *
 * LOAD ORDER:
 * This must be loaded AFTER wp-element but BEFORE react-query.js
 */

// Get React from the global window object (provided by WordPress via wp-element)
const React = window.React;

if (!React) {
	throw new Error('React is not available. Make sure wp-element is enqueued before this script.');
}

/**
 * Create JSX runtime exports that React Query expects.
 * These functions translate modern JSX transform calls to classic React.createElement calls.
 *
 * Note: Both jsx() and jsxs() do the same thing - the 's' variant is used when there are
 * multiple static children, but the behavior is identical for our purposes.
 */
window.ReactJSXRuntime = {
	/**
	 * Create a React element (equivalent to React.createElement)
	 * @param {string|Function} type - Element type (e.g., 'div', Component)
	 * @param {Object} props - Props including children
	 * @param {string|number} key - Optional key
	 */
	jsx: function (type, props, key) {
		const { children, ...rest } = props || {};
		return React.createElement(type, { ...rest, key }, children);
	},

	/**
	 * Create a React element with static children (same as jsx for our purposes)
	 * @param {string|Function} type - Element type
	 * @param {Object} props - Props including children
	 * @param {string|number} key - Optional key
	 */
	jsxs: function (type, props, key) {
		const { children, ...rest } = props || {};
		return React.createElement(type, { ...rest, key }, children);
	},

	/**
	 * React Fragment component for grouping elements without extra DOM nodes
	 */
	Fragment: React.Fragment,
};
